import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Tags, Plus, Pencil, X } from "lucide-react";
import api from "../../api";

// Clinician-added fields on the patient profile ("Preferred name", "Referred
// by", "School"...), care team only. Removing one hides it; every change is
// in the profile history.
const SUGGESTED = ["Preferred name", "Referred by", "School / college", "GP / family doctor", "Interpreter needed"];
const inputClass = "w-full rounded-lg border border-secondary-300 p-2 text-sm";

export default function PatientCustomFields({ patientId }) {
  const [fields, setFields] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, label, value}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getPatientCustomFields(patientId).then((r) => setFields(r.data || [])).catch(() => setFields([]));
  }, [patientId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { label: editing.label, value: editing.value };
      const r = editing.id ? await api.updatePatientCustomField(patientId, editing.id, body) : await api.addPatientCustomField(patientId, body);
      setFields(r.data || []);
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save the field.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove "${f.label}" from this patient's profile?`)) return;
    try {
      const r = await api.updatePatientCustomField(patientId, f.id, { remove: true });
      setFields(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't remove the field.");
    }
  };

  const unused = SUGGESTED.filter((s) => !(fields || []).some((f) => f.label.toLowerCase() === s.toLowerCase()));

  return (
    <div className="rounded-xl bg-white border border-secondary-100 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Tags className="w-4 h-4 text-primary-600" />
        <p className="text-sm font-bold text-secondary-900">Your own fields</p>
        {!editing && (
          <button type="button" onClick={() => setEditing({ label: "", value: "" })}
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
            <Plus className="w-3.5 h-3.5" /> Add field
          </button>
        )}
      </div>
      <p className="text-xs text-secondary-500 mb-3">Anything else worth keeping on the profile. Only the care team sees these.</p>

      {editing && (
        <div className="rounded-lg bg-secondary-50 p-3 space-y-2 mb-3">
          <input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} maxLength={80}
            placeholder="Field name, e.g. Preferred name" aria-label="Field name" className={inputClass} />
          {!editing.id && !editing.label && unused.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {unused.map((s) => (
                <button key={s} type="button" onClick={() => setEditing({ ...editing, label: s })}
                  className="rounded-full border border-secondary-300 bg-white px-2.5 py-0.5 text-xs text-secondary-700 hover:border-primary-400">{s}</button>
              ))}
            </div>
          )}
          <textarea value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} maxLength={2000}
            placeholder="Value" aria-label="Value" className={`${inputClass} min-h-[60px]`} />
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving || !editing.label.trim()}
              className="rounded-lg bg-primary-600 text-white text-xs font-bold px-3 py-2 disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={() => setEditing(null)} className="rounded-lg border border-secondary-300 text-xs px-3 py-2">Cancel</button>
          </div>
        </div>
      )}

      {fields === null ? <p className="text-sm text-secondary-500">Loading…</p>
        : fields.length === 0 ? (!editing && <p className="text-sm text-secondary-400">None yet.</p>) : (
        <dl className="divide-y divide-secondary-100">
          {fields.map((f) => (
            <div key={f.id} className="flex items-start gap-2 py-2">
              <div className="flex-1 min-w-0">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-secondary-400">{f.label}</dt>
                <dd className={`text-sm break-words whitespace-pre-line ${f.value ? "text-secondary-800" : "text-secondary-400"}`}>{f.value || "-"}</dd>
              </div>
              <button type="button" onClick={() => setEditing({ id: f.id, label: f.label, value: f.value || "" })} aria-label={`Edit ${f.label}`}
                className="p-1.5 rounded-full text-secondary-400 hover:text-primary-600 hover:bg-primary-50"><Pencil className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => remove(f)} aria-label={`Remove ${f.label}`}
                className="p-1.5 rounded-full text-secondary-400 hover:text-red-600 hover:bg-red-50"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
